/**
 * Экран-трекинг ПОДАННОГО заказа клиента (orders-core, D9; перестроен centrika-docs!41):
 * ОБЩАЯ ЧАСТЬ страницы — шапка: заголовок заказа со статусом, «Заказ № … от ДД.ММ.ГГГГ», справа
 * «Открыть анкету/заявку» (read-only), «Отменить заказ» и «Выгрузить в Excel», под ними — степпер
 * «Ход работ» без карточки и заголовка (ТЗ 26.10.2026, п.6.3.1, ответ заказчицы В24: маршрут
 * заявки и кнопка её открытия — наверху) — плюс РАБОЧАЯ ОБЛАСТЬ своей услуги. Отдельной кнопки
 * «К моим заказам» на странице нет.
 *
 * Общие блоки («Реквизиты на дату создания заказа», «Состав заказа»,
 * «Документы по заказу») описаны здесь один раз и переиспользуются рабочими областями:
 * набор блоков услуги виден в одном месте, а не собирается из условий по всей разметке.
 * У обучения «Документов по заказу» и блока результата НЕТ — итоговый документ принадлежит
 * ПРОГРАММЕ и живёт в её группе состава заказа обучения (там же клиент управляет согласием ПД);
 * сводки «Состав заказа» у обучения тоже нет — её заменяет сам состав заказа обучения.
 *
 * Только для просмотра: редактирования объёма и отправки на проверку здесь нет (подача — из анкеты).
 * Заказы в фазе данных (draft|changes_requested) сюда не попадают — авторедирект в анкету услуги.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { Download } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ApiError,
  ordersApi,
  profriskApi,
  soutApi,
  trainingApi,
  type TrainingSelectedEmployee,
  type OrderDetail,
  type ProfriskResult,
  type SoutAnketa,
  type SoutResult,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  clientBadge,
  clientOrderActions,
  COMPOSITION_ROW_LABELS,
  compositionSummary,
  serviceAnketaPath,
  serviceLabel,
} from '@/lib/orders'
import { exportOrderToExcel } from '@/lib/excel'
import { RISK_LEVEL_VIEW } from '@/lib/profrisk'
import { EcologyOrderArea } from '@/ecology/order/client-area'
import { CompanyProfileView } from '@/company/company-profile-view'
import { TrainingOrderComposition } from '@/training/training-order-composition'
import { TrainingPaperwork } from '@/training/training-paperwork'
import {
  TrainingChangeEditor,
  TrainingChangeHistory,
  TrainingChangeNotice,
  type ChangeComposition,
} from '@/training/training-change'
import { compositionKey } from '@/training/training-request'
import { OrderStageStepper } from '@/orders/order-stage-stepper'
import { CancelOrderDialog, CancellationNotice, RestoreOrderDialog } from '@/orders/order-cancel'
import { OrderQuestionButton } from '@/questions/ask-question'
import { useAuth } from '@/app/auth-context'
import { PageHead } from '@/design-system/components/layout/page-head'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/design-system/components/primitives/tooltip'
import { Alert } from '@/design-system/components/feedback/alert'
import { ListItem } from '@/design-system/components/data/list-item'
import { DocIcon } from '@/design-system/components/feedback/doc-icon'

function formatDate(iso?: string): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * «Ход работ»: степпер этапов — единственное представление их порядка и состояния, тот же, что
 * на командной карточке (замечание заказчика: текстовый список этапов под степпером его
 * дублировал). Под этапом — дата завершения или ориентир «до …» (см. `OrderStageStepper`).
 * Стоит в шапке страницы под заголовком заказа, без карточки (ТЗ п.6.3.1); видимого заголовка у
 * блока нет — название «Ход работ» остаётся именем области для скринридеров.
 */
function WorkProgress({ order }: { order: OrderDetail }) {
  return (
    <section aria-label="Ход работ" className="mb-5">
      <OrderStageStepper stages={order.stages} />
    </section>
  )
}

function OrderDocuments({ order }: { order: OrderDetail }) {
  return (
    <Card header="Документы по заказу">
      {order.documents.length === 0 ? (
        <p className="py-2 text-txt-secondary">
          Документы появятся по мере готовности — пока ожидаются.
        </p>
      ) : (
        order.documents.map((d) => (
          <ListItem
            key={d.id}
            leading={<DocIcon />}
            name={d.title}
            meta={`${d.typeName} · ${formatDate(d.date)}`}
            action={
              <Button asChild size="sm" variant="secondary">
                <a href={d.downloadUrl} download>
                  Скачать
                </a>
              </Button>
            }
          />
        ))
      )}
    </Card>
  )
}

/**
 * «Реквизиты на дату создания заказа» (F2-07, замечание менеджера): read-only снимок
 * реквизитов организации, зафиксированный НА МОМЕНТ создания заказа — правки профиля
 * компании (раздел «Настройки») после создания заказа сюда не попадают. Полный
 * стабильный набор полей (см. CompanyProfileView) — технически пустые поля
 * показываются как «—», не скрываются; состав/порядок строк от заполненности не
 * зависит. Блока нет только у клиента-физлица (реквизитов организации не бывает).
 */
function OrderRequisitesSnapshot({ order }: { order: OrderDetail }) {
  if (!order.snapshotRequisites) return null
  // По умолчанию свёрнуты, развернуть можно (ТЗ п.6.2) — как на странице заказа у команды.
  return (
    <CompanyProfileView
      profile={order.snapshotRequisites}
      title="Реквизиты на дату создания заказа"
      hint="Зафиксированы при создании заказа и не меняются последующей правкой профиля компании."
      collapsible
      defaultCollapsed
    />
  )
}

/** Строка «label — value» в блоке «Состав заказа» (по референсу: ярлык слева, значение справа). */
function CompositionRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-line py-2 last:border-0">
      <span className="text-txt-secondary">{label}</span>
      <span className="text-right text-txt">{value}</span>
    </div>
  )
}

/**
 * «Состав заказа» — вычисляемая read-only сводка (D10): счётчик размера (рабочих мест/слушателей/
 * должностей) и список подразделений. Значения считает бэкенд из анкеты услуги.
 *
 * Строки «Ответственный от Центрики» здесь НЕТ (centrika-docs!49): ответственный скрыт от
 * клиента, сервер его клиенту не отдаёт — поэтому нет его и в клиентской выгрузке заказа
 * (`lib/excel.ts`). На командной странице заказа он назначается, отображается и выгружается.
 */
function OrderComposition({ order }: { order: OrderDetail }) {
  const c = order.composition
  return (
    <Card header="Состав заказа">
      <CompositionRow label={COMPOSITION_ROW_LABELS[order.service] ?? 'Объектов'} value={String(c.count)} />
      <CompositionRow
        label="Подразделения"
        value={c.departments.length > 0 ? c.departments.join(', ') : '—'}
      />
    </Card>
  )
}

/**
 * «Результат услуги» (F2-15, замечание менеджера): показывается клиенту ТОЛЬКО когда
 * `order.dataStatus === 'done'` (гейт — в родителе, `OrderDetailPage`) — до этого
 * бэкенд отвечает 409 (данных ещё нет как факта). Состав блока — по типу услуги;
 * пустые значения полей — «—» (не скрываются).
 */
function SoutResults({ order }: { order: OrderDetail }) {
  const [anketa, setAnketa] = useState<SoutAnketa | null>(null)
  const [results, setResults] = useState<SoutResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    Promise.all([soutApi.getAnketa(order.id, controller.signal), soutApi.results(order.id, controller.signal)])
      .then(([ank, res]) => {
        if (controller.signal.aborted) return
        setAnketa(ank)
        setResults(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить результат СОУТ'))
      })
    return () => controller.abort()
  }, [order.id])

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
      </Alert>
    )
  }
  if (!results) {
    return (
      <Card header="Результат СОУТ" className="mt-4">
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      </Card>
    )
  }
  return (
    <Card header="Результат СОУТ по местам">
      {results.length === 0 ? (
        <p className="py-2 text-txt-secondary">Результатов пока нет.</p>
      ) : (
        results.map((r) => {
          const line = anketa?.lines.find((l) => l.id === r.lineId || l.workplaceId === r.workplaceId)
          return (
            <ListItem
              key={r.id}
              name={line?.conditions.position || 'Рабочее место'}
              meta={
                <>
                  {line?.conditions.departmentName ? `${line.conditions.departmentName} · ` : ''}
                  Класс УТ: {r.hazardClass || '—'} · Проведено: {formatDate(r.conductedOn)} · Действует до:{' '}
                  {r.perpetual ? 'бессрочно' : formatDate(r.validUntil)}
                </>
              }
            />
          )
        })
      )}
    </Card>
  )
}

function ProfriskResults({ order }: { order: OrderDetail }) {
  const [results, setResults] = useState<ProfriskResult[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    profriskApi
      .results(order.id, controller.signal)
      .then((res) => {
        if (!controller.signal.aborted) setResults(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить результат профрисков'))
      })
    return () => controller.abort()
  }, [order.id])

  if (error) {
    return (
      <Alert variant="danger" className="mt-4">
        {error}
      </Alert>
    )
  }
  if (!results) {
    return (
      <Card header="Результат профрисков" className="mt-4">
        <p className="py-2 text-txt-secondary">Загрузка…</p>
      </Card>
    )
  }
  return (
    <Card header="Результат оценки профрисков">
      {results.length === 0 ? (
        <p className="py-2 text-txt-secondary">Результатов пока нет.</p>
      ) : (
        results.map((r) => {
          const view = RISK_LEVEL_VIEW[r.riskLevel]
          return (
            <ListItem
              key={r.id}
              name={r.position || '—'}
              meta={`${r.department || '—'} · Оценено: ${formatDate(r.assessedOn)}`}
              action={<Badge status={view.role}>{view.label}</Badge>}
            />
          )
        })
      )}
    </Card>
  )
}

/**
 * Подпись кнопки открытия анкеты/заявки услуги в шапке страницы (centrika-docs!41, ТЗ п.6.3.1).
 * Поданный заказ: анкета доступна только для просмотра; у обучения открывается состояние
 * отправленного заказа с актуальными согласиями сотрудников. Экологии — мастер, пока его можно
 * править; null — кнопки нет.
 */
function openAnketaLabel(order: OrderDetail): string | null {
  switch (order.service) {
    case 'sout':
      return 'Открыть анкету СОУТ'
    case 'profrisk':
      return 'Открыть анкету профрисков'
    case 'training':
      return 'Открыть заявку на обучение'
    case 'ecology':
      return order.canEdit ? 'Открыть мастер заказа' : null
  }
  return null
}

/** Рабочая область заказа СОУТ: результат — только у готового заказа (F2-15). */
function SoutOrderArea({ order }: { order: OrderDetail }) {
  return (
    <>
      <Grid cols={2}>
        <div className="flex flex-col gap-4">
          <OrderRequisitesSnapshot order={order} />
        </div>
        <div className="flex flex-col gap-4">
          <OrderComposition order={order} />
          {order.dataStatus === 'done' ? <SoutResults order={order} /> : null}
          <OrderDocuments order={order} />
        </div>
      </Grid>
    </>
  )
}

/** Рабочая область заказа профрисков: результат — только у готового заказа (F2-15). */
function ProfriskOrderArea({ order }: { order: OrderDetail }) {
  return (
    <>
      <Grid cols={2}>
        <div className="flex flex-col gap-4">
          <OrderRequisitesSnapshot order={order} />
        </div>
        <div className="flex flex-col gap-4">
          <OrderComposition order={order} />
          {order.dataStatus === 'done' ? <ProfriskResults order={order} /> : null}
          <OrderDocuments order={order} />
        </div>
      </Grid>
    </>
  )
}

/**
 * Рабочая область заказа обучения (centrika-docs!41, перекомпонована по замечанию заказчика):
 * блоки друг под другом в порядке важности — главное в заказе — состав заказа обучения (счётчик
 * слушателей в заголовке, слушатели по программам и подразделениям), в конце — второстепенные
 * реквизиты на дату создания заказа. «Ход работ» — выше, в общей части страницы. Сводки «Состав заказа» нет: её счётчик — в
 * заголовке состава, подразделения — группами слушателей. Карточек «Документы по заказу» и
 * «Результат обучения» здесь нет: итоговый документ принадлежит программе и лежит в её группе
 * состава, там же — даты и согласия ПД. Договор и счёт менеджера (training-order-filling) — первым
 * блоком, если они приложены.
 */
/** Данные редактора изменений поданного заказа: состав, слушатели, позиции с итоговым документом. */
interface ChangeEditorData {
  initial: ChangeComposition
  employees: TrainingSelectedEmployee[]
  locked: Set<string>
  /** Программы с выпущенным итоговым документом. */
  lockedPrograms: Set<string>
}

/**
 * Изменения состава поданного заказа обучения (training-change-approval; ТЗ п.1.4, В20): блок
 * «Изменения на согласовании» с решением по ним или «Изменить состав» — редактор вместо состава
 * заказа. После решения или предложения страница получает обновлённый заказ, состав перечитывается.
 */
function useTrainingChange(order: OrderDetail, onOrderChanged: (next: OrderDetail) => void) {
  const [editor, setEditor] = useState<ChangeEditorData | null>(null)
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)
  const [version, setVersion] = useState(0)
  // Появились или решены изменения на согласовании — редактор с прежним составом закрывается.
  const changeAt = order.trainingChange?.at
  useEffect(() => {
    setEditor(null)
  }, [changeAt])

  function changed(next: OrderDetail) {
    setEditor(null)
    setVersion((v) => v + 1)
    onOrderChanged(next)
  }

  async function open() {
    setOpenError(null)
    setOpening(true)
    try {
      const [wizard, composition] = await Promise.all([
        trainingApi.getWizard(order.id),
        trainingApi.getComposition(order.id),
      ])
      const locked = new Set(
        (composition.items ?? [])
          .filter((it) => it.locked)
          .map((it) =>
            it.packageId ? compositionKey('package', it.packageId) : compositionKey('program', it.programId ?? ''),
          ),
      )
      setEditor({
        initial: { programs: wizard.programs, packages: wizard.packages },
        employees: wizard.selectedEmployees,
        locked,
        lockedPrograms: new Set(
          composition.groups.filter((g) => g.documentName || g.documentUrl).map((g) => g.programId),
        ),
      })
    } catch (err) {
      setOpenError(getErrorMessage(err, 'Не удалось открыть изменение состава'))
    } finally {
      setOpening(false)
    }
  }

  async function decide(action: 'approve' | 'reject' | 'withdraw', reason = '') {
    try {
      changed(
        await (action === 'approve'
          ? trainingApi.approveChange(order.id)
          : action === 'reject'
            ? trainingApi.rejectChange(order.id, reason)
            : trainingApi.withdrawChange(order.id)),
      )
    } catch (err) {
      // Решение уже приняли с другой стороны — страница приходит в соответствие с сервером.
      if (err instanceof ApiError && err.status === 409) {
        ordersApi.get(order.id).then(changed).catch(() => undefined)
      }
      throw err
    }
  }

  async function propose(items: Parameters<typeof trainingApi.proposeChange>[1]) {
    try {
      changed(await trainingApi.proposeChange(order.id, items))
    } catch (err) {
      // 409 — заказ изменился с другой стороны (например, Центрика уже предложила изменения):
      // страница перечитывает заказ и показывает его как есть, редактор закрывается.
      if (err instanceof ApiError && err.status === 409) {
        try {
          changed(await ordersApi.get(order.id))
          return
        } catch {
          // Перечитать не удалось — ошибка остаётся в редакторе.
        }
      }
      throw err
    }
  }

  return { editor, opening, openError, version, open, decide, propose, cancel: () => setEditor(null) }
}

/**
 * «Изменить состав» — в шапке страницы, вместо «Отменить заказ» (ответ заказчицы 04.11, п.11); что
 * можно изменить — подсказкой при наведении на кнопку, а не строкой под ней.
 */
export const CHANGE_COMPOSITION_HINT =
  'Добавить или убрать программу, изменить число мест, заменить слушателя — изменения согласует Центрика.'

function ChangeCompositionButton({ change }: { change: TrainingChangeState }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          disabled={change.opening}
          aria-description={CHANGE_COMPOSITION_HINT}
          onClick={() => void change.open()}
        >
          {change.opening ? 'Загрузка…' : 'Изменить состав'}
        </Button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{CHANGE_COMPOSITION_HINT}</TooltipContent>
    </Tooltip>
  )
}

type TrainingChangeState = ReturnType<typeof useTrainingChange>

function TrainingOrderArea({
  order,
  change,
  cancelAction,
}: {
  order: OrderDetail
  change: TrainingChangeState
  /** «Отменить заказ» — внизу, в строке реквизитов (ответ заказчицы 04.11, п.11). */
  cancelAction?: ReactNode
}) {
  return (
    <>
      <div className="flex flex-col gap-4">
        {/* Договор и счёт от менеджера (training-order-filling, В16) — только если они есть. */}
        <TrainingPaperwork documents={order.documents} />
        {order.trainingChange ? (
          <TrainingChangeNotice change={order.trainingChange} side="client" onAction={change.decide} />
        ) : null}
        {change.editor ? (
          <TrainingChangeEditor
            orderId={order.id}
            side="client"
            submitted
            initial={change.editor.initial}
            employees={change.editor.employees}
            locked={change.editor.locked}
            lockedPrograms={change.editor.lockedPrograms}
            propose={change.propose}
            onCancel={change.cancel}
          />
        ) : (
          <>
            {change.openError ? <Alert variant="danger">{change.openError}</Alert> : null}
            <TrainingOrderComposition key={change.version} orderId={order.id} mode="client" />
            {/* История изменений состава (ответ заказчицы 04.11 к п.1.4). */}
            <TrainingChangeHistory history={order.trainingChangeHistory} side="client" />
          </>
        )}
        {/* Реквизиты — строки «подпись — значение»: на всю ширину их части разъехались бы.
            У клиента-физлица реквизитов нет — пустую сетку (и отступ под ней) не выводим. */}
        {order.snapshotRequisites || cancelAction ? (
          <Grid cols={2}>
            <OrderRequisitesSnapshot order={order} />
            {cancelAction ? <div className="flex items-start justify-end">{cancelAction}</div> : null}
          </Grid>
        ) : null}
      </div>
    </>
  )
}

/** Диспетч рабочей области по услуге заказа. */
function OrderServiceArea({
  order,
  change,
  cancelAction,
}: {
  order: OrderDetail
  change: TrainingChangeState
  cancelAction?: ReactNode
}) {
  if (order.service === 'sout') return <SoutOrderArea order={order} />
  if (order.service === 'profrisk') return <ProfriskOrderArea order={order} />
  if (order.service === 'training')
    return <TrainingOrderArea order={order} change={change} cancelAction={cancelAction} />
  if (order.service === 'ecology') return <EcologyOrderArea order={order} />
  return null
}

/**
 * Загруженный заказ: шапка, плашка отмены, «Ход работ» и рабочая область услуги. Изменения состава
 * обучения (`useTrainingChange`) — здесь, а не в рабочей области: их кнопка «Изменить состав» стоит в
 * шапке (ответ заказчицы 04.11, п.11). Отмену у обучения кнопка «Отменить заказ» делает из строки
 * реквизитов внизу, у остальных услуг — из шапки, как раньше.
 */
function LoadedOrder({
  order,
  anketaLabel,
  badge,
  onOrderChanged,
  onQuestionCreated,
  onCancelClick,
  onRestoreClick,
}: {
  order: OrderDetail
  anketaLabel: string | null
  badge: ReturnType<typeof clientBadge> | null
  onOrderChanged: (next: OrderDetail) => void
  onQuestionCreated: () => void
  onCancelClick: () => void
  onRestoreClick: () => void
}) {
  const navigate = useNavigate()
  const change = useTrainingChange(order, onOrderChanged)
  const training = order.service === 'training'
  const cancelButton = order.canCancel ? (
    <Button variant="secondary" onClick={onCancelClick}>
      Отменить заказ
    </Button>
  ) : null
  return (
    <>
      <PageHead
        className="mb-4 flex-wrap"
        title={
          <span className="flex items-center gap-2.5">
            {order.composition.count > 0
              ? `${serviceLabel(order.service)} · ${compositionSummary(order.service, order.composition.count)}`
              : serviceLabel(order.service)}
            {badge ? <Badge status={badge.role}>{badge.label}</Badge> : null}
          </span>
        }
        // «Заказ № {индекс услуги}-{номер} от {дд.мм.гггг}» (centrika-docs!47).
        subtitle={`Заказ № ${order.number} от ${formatDate(order.createdAt)}`}
        actions={
          <div className="flex flex-wrap justify-end gap-2">
            {anketaLabel ? (
              <Button variant="secondary" onClick={() => navigate(serviceAnketaPath(order.id, order.service))}>
                {anketaLabel}
              </Button>
            ) : null}
            {/* Вопрос по заказу (client-questions; ТЗ п.9.7) — его получит ответственный. */}
            <OrderQuestionButton order={order} onCreated={onQuestionCreated} />
            {training ? (
              order.canProposeChange && !change.editor ? <ChangeCompositionButton change={change} /> : null
            ) : (
              cancelButton
            )}
            <Button
              variant="secondary"
              leftIcon={<Download className="size-4 stroke-current" aria-hidden />}
              onClick={() => exportOrderToExcel(order)}
            >
              Выгрузить в Excel
            </Button>
          </div>
        }
      />

      {order.cancellation ? (
        <CancellationNotice
          cancellation={order.cancellation}
          side="client"
          canRestore={order.canRestore === true}
          onRestore={onRestoreClick}
          className="mb-4"
        />
      ) : null}

      {/* Плашка отмены — сразу под шапкой, этапы — под ней. */}
      <WorkProgress order={order} />

      <OrderServiceArea order={order} change={change} cancelAction={training ? cancelButton : undefined} />
    </>
  )
}

export function OrderDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [order, setOrder] = useState<OrderDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // Отмена и восстановление (order-cancel-restore): окна подтверждения; меняется и счётчик у «Мои
  // заказы» — контекст кабинета перечитывается.
  const { refresh } = useAuth()
  const [cancelOpen, setCancelOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)

  // Отказ сервера (заказ уже отменили или завершили с другой стороны) — ошибка в окне, а страница
  // перечитывает заказ, чтобы кнопки соответствовали его статусу.
  async function reloadAfterFailure(id: string) {
    try {
      setOrder(await ordersApi.get(id))
    } catch {
      // Ошибка уже показана в окне; страница остаётся как была.
    }
  }

  async function cancelOrder(reason: string) {
    if (!order) return
    try {
      const next = await ordersApi.cancel(order.id, reason)
      setCancelOpen(false)
      setOrder(next)
      void refresh()
    } catch (err) {
      void reloadAfterFailure(order.id)
      throw err
    }
  }

  // Восстановленный заказ в фазе данных («На заполнении», «Требуются правки») ведётся в анкете
  // услуги — туда и переходим, как при открытии такого заказа.
  async function restoreOrder() {
    if (!order) return
    let next: OrderDetail
    try {
      next = await ordersApi.restore(order.id)
    } catch (err) {
      void reloadAfterFailure(order.id)
      throw err
    }
    setRestoreOpen(false)
    void refresh()
    if (clientOrderActions(next.dataStatus).canContinue) {
      navigate(serviceAnketaPath(next.id, next.service), { replace: true })
      return
    }
    setOrder(next)
  }

  useEffect(() => {
    if (!id) return
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    ordersApi
      .get(id, controller.signal)
      .then((res) => {
        if (controller.signal.aborted) return
        // Заказ в фазе данных (черновик, «На заполнении», возврат) ведётся в анкете услуги —
        // авторедирект (D9), не показываем экран-трекинг. replace: чтобы «Назад» не возвращал
        // на пустой трекинг.
        // abort до navigate: финализатор уйдёт в ветку aborted и не дёрнет setLoading на
        // уходящем компоненте (без мигания «Загрузка…»).
        if (clientOrderActions(res.dataStatus).canContinue) {
          controller.abort()
          navigate(serviceAnketaPath(res.id, res.service), { replace: true })
          return
        }
        setOrder(res)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [id, navigate])

  const badge = order ? clientBadge(order.dataStatus) : null
  const anketaLabel = order ? openAnketaLabel(order) : null

  return (
    <div>
      {error ? (
        <Alert variant="danger">{error}</Alert>
      ) : loading || !order ? (
        <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
      ) : (
        <>
          <LoadedOrder
            order={order}
            anketaLabel={anketaLabel}
            badge={badge}
            onOrderChanged={(next) => {
              setOrder(next)
              // Изменения Центрики, ждущие клиента, — в счётчике у «Мои заказы».
              void refresh()
            }}
            onQuestionCreated={() => void refresh()}
            onCancelClick={() => setCancelOpen(true)}
            onRestoreClick={() => setRestoreOpen(true)}
          />
          <CancelOrderDialog
            open={cancelOpen}
            side="client"
            orderNumber={order.number}
            onClose={() => setCancelOpen(false)}
            onConfirm={cancelOrder}
          />
          <RestoreOrderDialog
            open={restoreOpen}
            fromStatus={order.cancellation?.fromStatus}
            onClose={() => setRestoreOpen(false)}
            onConfirm={restoreOrder}
          />
        </>
      )}
    </div>
  )
}
